import {
  COPIAS_MAXIMAS,
  ESPERA_PELO_AGENTE_MIN,
  LAYOUTS,
  salvarImpressoraSchema,
  type ConexaoDeImpressora,
  type EstadoGravadoDaImpressora,
  type ImpressoraNaRede,
  type LayoutId,
  type SalvarImpressora,
} from "@foto/shared";
import { comVerbo } from "./tempo.js";

/**
 * A impressora na tela, sem React: como ler o estado que o servidor guardou, se dá para imprimir agora, e a
 * frase de confirmação antes de gastar papel e fita.
 *
 * Separado do componente pelo mesmo motivo de `folhas.ts` e `fila.ts`: são regras, e regra se testa sem
 * navegador. A de "dá para imprimir?" **espelha** a do servidor (`situacaoDaImpressora`, em
 * apps/api/src/services/impressao.ts) — a tela não pode prometer o que o worker vai recusar, nem recusar o
 * que ele aceitaria.
 */

export type TomDoSelo = "brasa" | "projecao" | "fixado" | "descartar" | "quieto";

/** O agente do notebook do evento (#109), como a tela o vê. A máquina e a impressora na rede, só o admin. */
export interface AgenteNaTela {
  conectado: boolean;
  vistoEm: string | null;
  /** Quando este agente foi ligado (o pareamento). */
  ligadoEm?: string | null;
  maquina: string | null;
  impressoraNaRede: ImpressoraNaRede | null;
}

/** O que a tela precisa de uma impressora; é o que `impressao.impressoras` devolve. */
export interface ImpressoraDaTela {
  id: number;
  nome: string;
  donoId: number;
  donoNome: string;
  /** Sem ela, `direta`: o servidor fala com a impressora. */
  conexao?: ConexaoDeImpressora;
  /** Só o admin recebe o endereço. */
  ippUri: string | null;
  /** `null` é "nunca testada". */
  estado: EstadoGravadoDaImpressora | null;
  estadoEm: string | null;
  /** Pelo agente: `null` é "nenhum agente ligado". */
  agente?: AgenteNaTela | null;
}

// ─── O estado, em português ───────────────────────────────────────────────────

/** `-warning` e `-report` avisam, não impedem: a impressora com pouca fita continua imprimindo. */
const SO_AVISO = /-(warning|report)$/;

/** Os motivos que impedem imprimir, em português. O que não está aqui aparece como a impressora o disse. */
const MOTIVOS_QUE_IMPEDEM: [RegExp, string][] = [
  [/^media-(empty|needed)/, "sem papel"],
  [/^media-jam/, "papel enroscado"],
  [/^(door|cover)-open/, "tampa aberta"],
  [/^(marker-supply|toner|ink)-empty/, "sem fita"],
  [/^(offline|shutdown)/, "desligada"],
  [/^paused/, "pausada"],
  [/^input-tray-missing/, "bandeja de papel fora do lugar"],
];

/** Os avisos que a casa entende; os demais não assustam ninguém à toa. */
const MOTIVOS_DE_AVISO: [RegExp, string][] = [
  [/^media-low/, "O papel está acabando."],
  [/^(marker-supply|toner|ink)-low/, "A fita está acabando."],
];

function problemaEmPortugues(motivo: string): string {
  const par = MOTIVOS_QUE_IMPEDEM.find(([padrao]) => padrao.test(motivo));
  if (par) return par[1];
  // Melhor o termo da impressora do que uma tradução inventada.
  const limpo = motivo.replace(/-error$/, "").replace(/-/g, " ");
  return `a impressora informou “${limpo}”`;
}

export function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** "93.6" → "93,6": medida do papel como se escreve no Brasil. */
export const numeroEmPortugues = (n: string) => n.replace(".", ",");

/** `jpn_hagaki_100x148mm` → "postal 100 × 148 mm". O que não se reconhece volta legível. */
export function nomeDoPapel(token: string): string {
  const medida = /(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)(mm|in)\b/i.exec(token);
  const dimensao = medida
    ? `${numeroEmPortugues(medida[1]!)} × ${numeroEmPortugues(medida[2]!)} ${medida[3]!.toLowerCase() === "in" ? "pol" : "mm"}`
    : null;
  if (/hagaki/i.test(token)) return dimensao ? `postal ${dimensao}` : "postal";
  return dimensao ?? token.replace(/[_-]/g, " ");
}

/** O papel da folha: o postal de 100 × 148 mm, em qualquer orientação. */
export function ehPapelPostal(token: string): boolean {
  if (/hagaki/i.test(token)) return true;
  const medida = /(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)mm\b/i.exec(token);
  if (!medida) return false;
  const [a, b] = [Number(medida[1]), Number(medida[2])];
  const perto = (x: number, alvo: number) => Math.abs(x - alvo) <= 1;
  return (perto(a, 100) && perto(b, 148)) || (perto(a, 148) && perto(b, 100));
}

export type Situacao =
  | "desconhecida"
  | "sem-contato"
  | "pronta"
  | "ocupada"
  | "parada"
  /** Pelo agente, nenhum agente ligado: ninguém vai pegar a folha. */
  | "sem-agente"
  /** Pelo agente, o agente não dá sinal: a folha espera por ele. */
  | "agente-desligado";

export interface LeituraDaImpressora {
  situacao: Situacao;
  /** O que o selo diz. */
  rotulo: string;
  tom: TomDoSelo;
  /** A frase sob o selo: o papel carregado, o que a ocupa, ou a mensagem do servidor como veio. */
  detalhe: string | null;
  /** O que impede imprimir, em minúsculas e sem ponto ("sem papel"): quem escreve a frase é a tela. */
  problemas: string[];
  /** O que não impede, mas convém saber. Frases completas. */
  avisos: string[];
}

/** O estado guardado, lido para a tela. `null` é a impressora que nunca foi testada. */
export function lerEstado(estado: EstadoGravadoDaImpressora | null): LeituraDaImpressora {
  if (estado === null) {
    return {
      situacao: "desconhecida",
      rotulo: "Ainda não verificada",
      tom: "quieto",
      detalhe: null,
      problemas: [],
      avisos: [],
    };
  }

  if (!estado.alcancavel) {
    return {
      situacao: "sem-contato",
      rotulo: "Sem contato",
      tom: "descartar",
      // Como veio do servidor: é ela que explica a rede e lembra do app da Canon.
      detalhe: estado.mensagem,
      problemas: [],
      avisos: [],
    };
  }

  const avisos: string[] = [];
  for (const motivo of estado.motivos) {
    if (!SO_AVISO.test(motivo)) continue;
    const par = MOTIVOS_DE_AVISO.find(([padrao]) => padrao.test(motivo));
    if (par && !avisos.includes(par[1])) avisos.push(par[1]);
  }
  if (estado.papel.length > 0 && !estado.papel.some(ehPapelPostal)) {
    // O papel carregado já aparece sob o selo; aqui só o que há de errado nele.
    avisos.push(
      "Esse papel não é o postal de 100 × 148 mm da folha: confira a bandeja antes de imprimir.",
    );
  }

  const problemas = estado.motivos.filter((m) => !SO_AVISO.test(m)).map(problemaEmPortugues);
  if (!estado.aceitandoTrabalhos) problemas.unshift("não está aceitando trabalhos");
  if (problemas.length === 0 && estado.estado === "stopped") problemas.push("está parada");
  if (problemas.length > 0) {
    return {
      situacao: "parada",
      rotulo: "Parada",
      tom: "descartar",
      detalhe: `${capitalizar([...new Set(problemas)].join(", "))}.`,
      problemas: [...new Set(problemas)],
      avisos,
    };
  }

  if (estado.estado === "processing" || estado.filaDaImpressora > 0) {
    const n = estado.filaDaImpressora;
    return {
      situacao: "ocupada",
      rotulo: "Ocupada",
      tom: "projecao",
      detalhe:
        estado.estado === "processing"
          ? "Está imprimindo outro trabalho."
          : `Há ${n} ${n === 1 ? "trabalho" : "trabalhos"} na fila dela.`,
      problemas: [],
      avisos,
    };
  }

  const papel = [...new Set(estado.papel.map(nomeDoPapel))];
  return {
    situacao: "pronta",
    rotulo: "Pronta",
    tom: "fixado",
    detalhe: papel.length > 0 ? `Papel ${papel.join(", ")}.` : null,
    problemas: [],
    avisos,
  };
}

/**
 * A impressora lida para a tela, contando o agente (#109): pelo agente, o estado que ele mandou só vale com ele
 * de pé — com ele desligado, uma leitura "Pronta" de uma hora atrás mentiria.
 */
export function lerImpressora(
  impressora: Pick<ImpressoraDaTela, "estado" | "conexao" | "agente">,
): LeituraDaImpressora {
  if (impressora.conexao !== "agente") return lerEstado(impressora.estado);

  const agente = impressora.agente ?? null;
  if (!agente) {
    return {
      situacao: "sem-agente",
      rotulo: "Sem agente",
      tom: "quieto",
      detalhe:
        "Nenhum agente ligado a esta impressora. O administrador liga o agente no notebook do evento.",
      problemas: [],
      avisos: [],
    };
  }
  if (!agente.conectado) {
    return {
      situacao: "agente-desligado",
      rotulo: "Agente desligado",
      tom: "descartar",
      detalhe: `O agente do notebook${agente.maquina ? ` (${agente.maquina})` : ""} não está dando sinal. Abra-o no notebook da impressora.`,
      problemas: [],
      avisos: [],
    };
  }
  return lerEstado(impressora.estado);
}

/** "Pronta · papel postal 100 × 148 mm": o estado numa linha, para listas e leitores de tela. */
export function resumoDoEstado(leitura: LeituraDaImpressora): string {
  if (leitura.situacao === "pronta" && leitura.detalhe) {
    return `${leitura.rotulo} · ${leitura.detalhe.replace(/\.$/, "").replace(/^Papel/, "papel")}`;
  }
  if (leitura.situacao === "ocupada" || leitura.situacao === "parada") {
    return `${leitura.rotulo} · ${(leitura.detalhe ?? "").replace(/\.$/, "").toLowerCase()}`;
  }
  return leitura.rotulo;
}

/** O estado guardado vale por pouco: a impressora é ligada e desligada, e a fila dela anda. */
export const VALIDADE_DO_ESTADO_MS = 60_000;

/** Vale a pena perguntar de novo à impressora? Nunca verificada, ou verificada há muito. */
export function estadoDesatualizado(
  estadoEm: string | null,
  agora: Date,
  validadeMs = VALIDADE_DO_ESTADO_MS,
): boolean {
  if (!estadoEm) return true;
  const t = new Date(estadoEm).getTime();
  return Number.isNaN(t) || agora.getTime() - t > validadeMs;
}

/** "Verificada há 2 min": de quando é o estado que a tela mostra, para ninguém confiar num de ontem. */
export function quandoFoiVerificada(estadoEm: string | null, agora: Date): string {
  return estadoEm ? comVerbo("Verificada", estadoEm, agora) : "Nunca verificada";
}

/**
 * Alguma impressora pode servir? Só a conhecidamente fora de alcance não conta: a que nunca foi testada
 * pode estar ótima. É o que decide se "Imprimir" ou "Baixar" é o botão principal da tela. Pelo agente, a do
 * agente desligado serve (a folha espera por ele); a sem agente nenhum, não.
 */
export function algumaPodeServir(
  impressoras: readonly Pick<ImpressoraDaTela, "estado" | "conexao" | "agente">[],
): boolean {
  return impressoras.some((i) => {
    const situacao = lerImpressora(i).situacao;
    return situacao !== "sem-contato" && situacao !== "sem-agente";
  });
}

/** O nome na lista de escolha; o admin, que vê as de vários clientes, precisa saber de quem é cada uma. */
export function rotuloDaImpressora(
  impressora: Pick<ImpressoraDaTela, "nome" | "donoNome">,
  mostrarDono: boolean,
): string {
  return mostrarDono ? `${impressora.nome} · ${impressora.donoNome}` : impressora.nome;
}

// ─── Pode imprimir? ───────────────────────────────────────────────────────────

export interface DecisaoDeImprimir {
  pode: boolean;
  /** Por que não — o que a tela diz junto ao botão desabilitado. */
  motivo: string | null;
  /** O que pode dar errado mesmo podendo. */
  aviso: string | null;
}

/**
 * Se o botão "Imprimir" vale agora, e o que a tela diz sobre isso.
 *
 * Sem contato e parada barram, com o porquê. **Nunca testada deixa tentar**: o servidor pode alcançar a
 * impressora mesmo sem ninguém ter pedido o teste, e barrar por falta de informação seria pior do que
 * deixar o trabalho falhar com a mensagem de rede. Ocupada também passa — o worker espera ela ficar livre.
 */
export function podeImprimir(entrada: {
  impressora: ImpressoraDaTela | null;
  fotosNaFolha: number;
  enviando: boolean;
  /** O que dizer quando não há nada a imprimir; a folha montada e a de marcadores têm remédios diferentes. */
  motivoSeVazia?: string;
}): DecisaoDeImprimir {
  const { impressora, fotosNaFolha, enviando } = entrada;

  if (enviando) return { pode: false, motivo: null, aviso: null };
  if (fotosNaFolha === 0) {
    return {
      pode: false,
      motivo:
        entrada.motivoSeVazia ?? "A folha está vazia: coloque ao menos uma foto antes de imprimir.",
      aviso: null,
    };
  }
  if (!impressora) {
    return { pode: false, motivo: "Escolha a impressora.", aviso: null };
  }

  const leitura = lerImpressora(impressora);
  const primeiroAviso = leitura.avisos[0] ?? null;

  switch (leitura.situacao) {
    case "sem-agente":
      return {
        pode: false,
        motivo:
          "Esta impressora ainda não tem um agente ligado. Peça ao administrador para ligá-lo no notebook do evento, ou use o app da Canon.",
        aviso: null,
      };
    case "agente-desligado":
      // Decisão do usuário (16/10/2026): a folha espera o agente, por pouco tempo, em vez de barrar o botão.
      return {
        pode: true,
        motivo: null,
        aviso: `O agente do notebook está desligado. A folha espera até ${ESPERA_PELO_AGENTE_MIN} minutos por ele: abra o agente no notebook da impressora.`,
      };
    case "sem-contato":
      return {
        pode: false,
        motivo:
          "Não dá para imprimir por aqui agora. Confira se a impressora está ligada e toque em Verificar agora, ou use o app da Canon.",
        aviso: null,
      };
    case "parada":
      return {
        pode: false,
        motivo: `Resolva antes de imprimir: ${leitura.problemas.join(", ")}. Depois, toque em Verificar agora.`,
        aviso: null,
      };
    case "desconhecida":
      return {
        pode: true,
        motivo: null,
        aviso:
          "Ainda não verifiquei esta impressora. Pode imprimir mesmo assim: se o sistema não a alcançar, o trabalho falha e você é avisado.",
      };
    case "ocupada":
      return {
        pode: true,
        motivo: null,
        aviso:
          "A impressora está ocupada. A folha entra na fila e sai quando ela ficar livre; se demorar demais, o envio falha e você é avisado.",
      };
    case "pronta":
      return { pode: true, motivo: null, aviso: primeiroAviso };
  }
}

// ─── Cópias e a confirmação ───────────────────────────────────────────────────

/** Cópias de 1 ao teto, sempre inteiras: o que não é número (ou é lixo) volta a 1. */
export function limitarCopias(valor: unknown): number {
  const n = typeof valor === "number" ? Math.trunc(valor) : Number.NaN;
  if (!Number.isFinite(n)) return 1;
  return Math.min(COPIAS_MAXIMAS, Math.max(1, n));
}

/** As opções do seletor de cópias: 1, 2, … até o teto. */
export const OPCOES_DE_COPIAS: readonly number[] = Array.from(
  { length: COPIAS_MAXIMAS },
  (_, i) => i + 1,
);

export function folhasPorExtenso(n: number): string {
  return `${n} ${n === 1 ? "folha" : "folhas"}`;
}

/**
 * A frase que fecha o painel antes de gastar papel e fita, e o que convém dizer junto.
 *
 * "4 por folha" é o formato (quantos espaços a folha tem), e os espaços sem foto saem em branco: a nota diz
 * quantos, porque uma folha de 4 com 3 fotos é a surpresa que dá para evitar aqui. `folha` só entra
 * quando há mais de uma montada, pois só a que está na tela vai.
 */
export function resumoDaConfirmacao(entrada: {
  copias: number;
  layout: LayoutId;
  fotosNaFolha: number;
  impressora: string;
  folha?: { numero: number; total: number };
}): { frase: string; notas: string[] } {
  const espacos = LAYOUTS[entrada.layout].celulas.length;
  const copias = limitarCopias(entrada.copias);
  const frase = `Vai imprimir ${folhasPorExtenso(copias)}, ${espacos} por folha, em ${entrada.impressora}.`;

  const notas = ["Cada folha gasta papel e fita da impressora."];
  const vazios = espacos - entrada.fotosNaFolha;
  if (vazios > 0) {
    notas.push(
      `${vazios === 1 ? "1 espaço está" : `${vazios} espaços estão`} sem foto e ${vazios === 1 ? "sai" : "saem"} em branco.`,
    );
  }
  if (entrada.folha && entrada.folha.total > 1) {
    notas.push(
      `Só a Folha ${entrada.folha.numero} (de ${entrada.folha.total}) é enviada agora; as outras você imprime separado.`,
    );
  }
  return { frase, notas };
}

// ─── Erros do servidor, em frase ──────────────────────────────────────────────

/**
 * Uma falha de chamada em frase que diz o que fazer.
 *
 * As recusas do servidor (`BAD_REQUEST`, `CONFLICT`, `NOT_FOUND`) já vêm em português, com o motivo, e
 * passam como vieram — é a mensagem que explica por que o endereço foi recusado. O que não é frase (o
 * JSON de uma validação, "Failed to fetch") é trocado por uma que a pessoa consiga usar.
 */
export function mensagemDoErro(
  e: unknown,
  padrao = "Não foi possível concluir. Tente de novo.",
): string {
  const erro = (e ?? null) as { message?: unknown; data?: { code?: unknown } | null } | null;
  const mensagem = typeof erro?.message === "string" ? erro.message.trim() : "";
  const codigo = typeof erro?.data?.code === "string" ? erro.data.code : null;

  if (codigo === "UNAUTHORIZED") return "Sua sessão acabou. Entre de novo para continuar.";
  if (codigo === "FORBIDDEN") return "Só o administrador pode fazer isso.";
  if (codigo === "INTERNAL_SERVER_ERROR" || codigo === "TIMEOUT") {
    return "O servidor teve um problema. Tente de novo em instantes; se continuar, avise o administrador.";
  }
  if (/failed to fetch|networkerror|load failed/i.test(mensagem)) {
    return "Sem conexão com o servidor. Confira a internet e tente de novo.";
  }
  // O que começa com `[` ou `{` é o JSON de uma validação: não é texto para ninguém.
  if (!mensagem || /^[[{]/.test(mensagem)) return padrao;
  return mensagem;
}

// ─── O formulário do admin ────────────────────────────────────────────────────

export interface ValoresDoFormulario {
  nome: string;
  conexao: ConexaoDeImpressora;
  ippUri: string;
  /** O id do cliente, como vem do `select`; vazio é "o próprio admin". */
  donoId: string;
}

export type ResultadoDoFormulario =
  { ok: true; dados: SalvarImpressora } | { ok: false; erros: { nome?: string; ippUri?: string } };

/**
 * Confere o formulário com o schema do servidor antes de enviar, para o erro aparecer no campo — e não
 * como o JSON de uma validação. O endereço só tem o tamanho conferido aqui; quem diz se ele serve (esquema,
 * loopback, nome sem ponto) é o servidor, e o motivo dele aparece como veio.
 */
export function validarFormulario(
  valores: ValoresDoFormulario,
  edicao?: { id: number },
): ResultadoDoFormulario {
  const candidato = {
    ...(edicao ? { id: edicao.id } : {}),
    nome: valores.nome,
    conexao: valores.conexao,
    // Pelo agente não há endereço: quem acha a impressora na rede do evento é o agente.
    ...(valores.conexao === "direta" ? { ippUri: valores.ippUri } : {}),
    // Só vale ao cadastrar: trocar de cliente uma impressora existente o servidor recusa.
    ...(!edicao && valores.donoId !== "" ? { donoId: Number(valores.donoId) } : {}),
  };

  const r = salvarImpressoraSchema.safeParse(candidato);
  if (r.success) return { ok: true, dados: r.data };

  const erros: { nome?: string; ippUri?: string } = {};
  for (const problema of r.error.issues) {
    const campo = problema.path[0];
    if ((campo !== "nome" && campo !== "ippUri") || erros[campo]) continue;
    // Os limites vêm do zod em inglês; só as mensagens que o schema escreveu já estão em português.
    erros[campo] =
      problema.code === "too_big"
        ? campo === "nome"
          ? "Use até 60 letras no nome."
          : "O endereço está longo demais."
        : problema.message;
  }
  return { ok: false, erros };
}

// ─── Ligar o agente (#109) ────────────────────────────────────────────────────

/** Quanto resta ao código que liga o agente: "vale por mais 9 min", ou vencido. */
export function validadeDoCodigo(
  expiraEm: string | Date,
  agora: Date,
): { vencido: boolean; texto: string } {
  const restante = new Date(expiraEm).getTime() - agora.getTime();
  if (!(restante > 0)) return { vencido: true, texto: "O código venceu." };
  const minutos = Math.ceil(restante / 60_000);
  return {
    vencido: false,
    texto: minutos <= 1 ? "Vale por menos de 1 minuto." : `Vale por mais ${minutos} minutos.`,
  };
}

/**
 * O agente se ligou com o código desta vez: o painel pode dizer que deu certo. Pela hora do pareamento, e não do
 * último sinal — o agente antigo, ainda de pé, dá sinal a cada 3 s, e não é ele que acabou de ser ligado.
 */
export function agenteLigadoDepoisDe(
  agente: AgenteNaTela | null | undefined,
  codigoGeradoEm: Date,
): boolean {
  if (!agente?.conectado || !agente.ligadoEm) return false;
  return new Date(agente.ligadoEm).getTime() >= codigoGeradoEm.getTime();
}

/** Onde baixar o agente de cada sistema, no servidor que serviu esta página. */
export function enderecoDoAgente(origem: string, sistema: "windows" | "linux"): string {
  return `${origem.replace(/\/+$/, "")}/api/agente/baixar/${sistema}`;
}

/**
 * A linha que instala e abre o agente no Linux: baixa para `~/.local/bin` (que as distribuições já põem no PATH),
 * dá permissão de execução e abre. Uma linha só, para colar no terminal sem errar.
 */
export function comandoDeInstalacaoNoLinux(origem: string): string {
  const destino = "~/.local/bin/foto-agente";
  return (
    `mkdir -p ~/.local/bin && curl -fL ${enderecoDoAgente(origem, "linux")} -o ${destino}` +
    ` && chmod +x ${destino} && ${destino}`
  );
}
